import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './Recommendations.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function DifficultyStars({ score }) {
  if (!score && score !== 0) return null;
  const rounded = Math.round(score);
  return (
    <span className="recommendations-stars" title={`Difficulty: ${score}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < rounded ? 'recommendations-star-filled' : 'recommendations-star-empty'}>
          ★
        </span>
      ))}
    </span>
  );
}

function BookCard({ book, actionStatus, onAddToLibrary, onWantToRead, onDismiss }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const desc = book.description;
  const truncDesc = desc && desc.length > 200 ? desc.slice(0, 200) + '…' : desc;

  return (
    <div className={`recommendations-card ${expanded ? 'recommendations-card--expanded' : ''} ${actionStatus[`dismiss-${book.bookId}`] === 'success' ? 'recommendations-card-dismissed' : ''}`}>
      <div className="recommendations-card-top" onClick={() => setExpanded(e => !e)} role="button" tabIndex={0}>
        <div className="recommendations-card-cover">
          <BookCover
            src={book.coverImageUrl}
            alt={book.title}
            className="recommendations-card-cover-img"
            emoji={book.bookType === 'fiction' ? '📖' : '📚'}
          />
        </div>
        <div className="recommendations-card-body">
          <h4 className="recommendations-card-title" title={book.title}>{book.title}</h4>
          <p className="recommendations-card-author">{book.author}</p>
          {book.bookType && (
            <span className="recommendations-card-tag">
              {book.bookType === 'fiction' ? '📖 Fiction' : '📚 Fact'}
            </span>
          )}
          <DifficultyStars score={book.difficultyScore} />
          {book.recommendationReason && (
            <p className="recommendations-card-reason">{book.recommendationReason}</p>
          )}
        </div>
        <span className="recommendations-card-chevron">{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div className="recommendations-card-detail">
          {truncDesc ? (
            <p className="recommendations-card-desc">{truncDesc}</p>
          ) : (
            <p className="recommendations-card-desc recommendations-card-desc--empty">No description available.</p>
          )}
          {book.pageCount > 0 && <span className="recommendations-card-pages">📄 {book.pageCount} pages</span>}
          <div className="recommendations-card-actions">
            <button
              className={`recommendations-btn ${actionStatus[`lib-${book.bookId}`] === 'success' ? 'success' : ''}`}
              onClick={(e) => { e.stopPropagation(); onAddToLibrary(book.bookId); }}
              disabled={actionStatus[`lib-${book.bookId}`] === 'loading' || actionStatus[`lib-${book.bookId}`] === 'success'}
            >
              {actionStatus[`lib-${book.bookId}`] === 'loading' ? '…' :
               actionStatus[`lib-${book.bookId}`] === 'success' ? `✓ ${t('bookClub.bookSearch.added')}` : `+ ${t('bookClub.recommendations.library')}`}
            </button>
            <button
              className={`recommendations-btn recommendations-btn-secondary ${actionStatus[`wtr-${book.bookId}`] === 'success' ? 'success' : ''}`}
              onClick={(e) => { e.stopPropagation(); onWantToRead(book.bookId); }}
              disabled={actionStatus[`wtr-${book.bookId}`] === 'loading' || actionStatus[`wtr-${book.bookId}`] === 'success'}
            >
              {actionStatus[`wtr-${book.bookId}`] === 'loading' ? '…' :
               actionStatus[`wtr-${book.bookId}`] === 'success' ? `✓ ${t('bookClub.bookSearch.added')}` : `📌 ${t('bookClub.bookSearch.wantToRead')}`}
            </button>
            <button
              className={`recommendations-btn recommendations-btn-dismiss ${actionStatus[`dismiss-${book.bookId}`] === 'success' ? 'success' : ''}`}
              onClick={(e) => { e.stopPropagation(); onDismiss(book.bookId); }}
              disabled={actionStatus[`dismiss-${book.bookId}`] === 'loading' || actionStatus[`dismiss-${book.bookId}`] === 'success'}
              title="Never suggest this book again"
            >
              {actionStatus[`dismiss-${book.bookId}`] === 'loading' ? '…' :
               actionStatus[`dismiss-${book.bookId}`] === 'success' ? `✓ ${t('bookClub.recommendations.hidden')}` : '🚫'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Recommendations({ factFictionMode, navigateTo }) {
  const { t } = useTranslation();
  const { instance } = useMsal();

  const [challengeBooks, setChallengeBooks] = useState([]);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState(null);

  const [finishedBooks, setFinishedBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [moreLikeBooks, setMoreLikeBooks] = useState([]);
  const [moreLikeLoading, setMoreLikeLoading] = useState(false);
  const [moreLikeError, setMoreLikeError] = useState(null);
  const [finishedLoading, setFinishedLoading] = useState(false);

  const [popularTracks, setPopularTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [tracksError, setTracksError] = useState(null);

  const [actionStatus, setActionStatus] = useState({});
  const [hasLangPrefs, setHasLangPrefs] = useState(true);

  // Check if user has set language preferences
  useEffect(() => {
    const checkLang = async () => {
      try {
        const d = await apiFetchJson(instance, '/api/readerprofile');
        setHasLangPrefs(!!d.preferredLanguages);
      } catch { /* ignore */ }
    };
    checkLang();
  }, [instance]);

  const loadChallengeBooks = useCallback(async () => {
    try {
      setChallengeLoading(true);
      setChallengeError(null);
      const data = await apiFetchJson(instance, '/api/recommendations/challenge-your-taste');
      setChallengeBooks(unwrap(data));
    } catch (e) {
      console.error('Failed to load challenge recommendations:', e);
      setChallengeError('Failed to load recommendations.');
    } finally {
      setChallengeLoading(false);
    }
  }, [instance]);

  const loadFinishedBooks = useCallback(async () => {
    try {
      setFinishedLoading(true);
      const data = await apiFetchJson(instance, '/api/userbookstatus?status=Finished');
      setFinishedBooks(unwrap(data));
    } catch (e) {
      console.error('Failed to load finished books:', e);
    } finally {
      setFinishedLoading(false);
    }
  }, [instance]);

  const loadPopularTracks = useCallback(async () => {
    try {
      setTracksLoading(true);
      setTracksError(null);
      const data = await apiFetchJson(instance, '/api/recommendations/popular-tracks');
      setPopularTracks(unwrap(data));
    } catch (e) {
      console.error('Failed to load popular tracks:', e);
      setTracksError('Failed to load popular tracks.');
    } finally {
      setTracksLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    loadChallengeBooks();
    loadFinishedBooks();
    if (factFictionMode === 'fact') {
      loadPopularTracks();
    }
  }, [loadChallengeBooks, loadFinishedBooks, loadPopularTracks, factFictionMode]);

  const loadMoreLikeThis = useCallback(async (bookId) => {
    if (!bookId) {
      setMoreLikeBooks([]);
      return;
    }
    try {
      setMoreLikeLoading(true);
      setMoreLikeError(null);
      const data = await apiFetchJson(instance, `/api/recommendations/more-like-this?bookId=${bookId}`);
      setMoreLikeBooks(unwrap(data));
    } catch (e) {
      console.error('Failed to load more-like-this:', e);
      setMoreLikeError('Failed to load recommendations.');
    } finally {
      setMoreLikeLoading(false);
    }
  }, [instance]);

  const handleBookSelect = useCallback((e) => {
    const bookId = e.target.value;
    setSelectedBookId(bookId);
    loadMoreLikeThis(bookId);
  }, [loadMoreLikeThis]);

  const addToLibrary = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'loading' }));
    try {
      await apiFetchJson(instance, '/api/userlibrary', {
        method: 'POST',
        body: JSON.stringify({ bookId, ownership: 'Wishlist' }),
      });
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'success' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const wantToRead = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`wtr-${bookId}`]: 'loading' }));
    try {
      await apiFetchJson(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status: 'WantToRead' }),
      });
      setActionStatus(prev => ({ ...prev, [`wtr-${bookId}`]: 'success' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`wtr-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const dismissBook = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status: 'NotInterested' }),
      });
      if (res.ok || res.status === 409) {
        // 409 means status already exists — update it
        if (res.status === 409) {
          await apiFetch(instance, `/api/userbookstatus/${bookId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'NotInterested' }),
          });
        }
        setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'success' }));
        // Remove from displayed lists after a short delay
        setTimeout(() => {
          setChallengeBooks(prev => prev.filter(b => b.bookId !== bookId));
          setMoreLikeBooks(prev => prev.filter(b => b.bookId !== bookId));
        }, 800);
      } else {
        setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'error' }));
      }
    } catch {
      setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const DIFFICULTY_COLORS = {
    Beginner: 'green',
    Intermediate: 'blue',
    Advanced: 'red',
  };

  return (
    <div className="recommendations-container">

      {!hasLangPrefs && (
        <div className="recommendations-lang-hint" onClick={() => navigateTo && navigateTo('profile')}>
          💡 Getting books in unexpected languages? Set your preferred languages in your <strong>Profile</strong> →
        </div>
      )}

      {/* Challenge Your Taste */}
      <section className="recommendations-section">
        <h2 className="recommendations-section-header">{t('bookClub.recommendations.challengeYourTaste')} 🎯</h2>
        {challengeLoading && (
          <div className="recommendations-loading">
            <span className="recommendations-spinner" />
            <p>{t('bookClub.recommendations.findingHorizons')}</p>
          </div>
        )}
        {challengeError && <div className="recommendations-error">{challengeError}</div>}
        {!challengeLoading && !challengeError && challengeBooks.length === 0 && (
          <div className="recommendations-empty">
            <span className="recommendations-empty-icon">🎯</span>
            <p>{t('bookClub.recommendations.rateFirst')}</p>
            {navigateTo && (
              <div>
                <button className="bookclub-nav-link" onClick={() => navigateTo('my-books')}>📖 {t('bookClub.common.goToMyBooks')}</button>{' '}
                <button className="bookclub-nav-link" onClick={() => navigateTo('search')}>🔎 {t('bookClub.common.searchBooks')}</button>
              </div>
            )}
          </div>
        )}
        {!challengeLoading && challengeBooks.length > 0 && (
          <div className="recommendations-scroll-row">
            {challengeBooks.map((book) => (
              <BookCard
                key={book.bookId}
                book={book}
                actionStatus={actionStatus}
                onAddToLibrary={addToLibrary}
                onWantToRead={wantToRead}
                onDismiss={dismissBook}
              />
            ))}
          </div>
        )}
      </section>

      {/* More Like This */}
      <section className="recommendations-section">
        <h2 className="recommendations-section-header">{t('bookClub.recommendations.moreLikeThis')} 📖</h2>
        {finishedLoading && (
          <div className="recommendations-loading">
            <span className="recommendations-spinner" />
            <p>{t('bookClub.recommendations.loadingBooks')}</p>
          </div>
        )}
        {!finishedLoading && finishedBooks.length === 0 && (
          <div className="recommendations-empty">
            <span className="recommendations-empty-icon">📖</span>
            <p>{t('bookClub.recommendations.rateFirst')}</p>
          </div>
        )}
        {!finishedLoading && finishedBooks.length > 0 && (
          <>
            <div className="recommendations-picker">
              <label htmlFor="recommendations-book-select" className="recommendations-picker-label">
                {t('bookClub.recommendations.pickBook')}
              </label>
              <select
                id="recommendations-book-select"
                className="recommendations-picker-select"
                value={selectedBookId}
                onChange={handleBookSelect}
              >
                <option value="">— Select a finished book —</option>
                {finishedBooks.map((entry) => (
                  <option key={entry.bookId || entry.id} value={entry.bookId || entry.id}>
                    {entry.book?.title || entry.title} — {entry.book?.author || entry.author}
                  </option>
                ))}
              </select>
            </div>
            {moreLikeLoading && (
              <div className="recommendations-loading">
                <span className="recommendations-spinner" />
                <p>{t('bookClub.recommendations.findingSimilar')}</p>
              </div>
            )}
            {moreLikeError && <div className="recommendations-error">{moreLikeError}</div>}
            {!moreLikeLoading && !moreLikeError && selectedBookId && moreLikeBooks.length === 0 && (
              <div className="recommendations-empty">
                <span className="recommendations-empty-icon">🔍</span>
                <p>{t('bookClub.recommendations.noSimilar')}</p>
              </div>
            )}
            {!moreLikeLoading && moreLikeBooks.length > 0 && (
              <div className="recommendations-scroll-row">
                {moreLikeBooks.map((book) => (
                  <BookCard
                    key={book.bookId}
                    book={book}
                    actionStatus={actionStatus}
                    onAddToLibrary={addToLibrary}
                    onWantToRead={wantToRead}
                    onDismiss={dismissBook}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Popular Learning Tracks — only in fact mode */}
      {factFictionMode === 'fact' && (
        <section className="recommendations-section">
          <h2 className="recommendations-section-header">{t('bookClub.recommendations.popularTracks')} 🛤️</h2>
          {tracksLoading && (
            <div className="recommendations-loading">
              <span className="recommendations-spinner" />
              <p>{t('bookClub.recommendations.loadingTracks')}</p>
            </div>
          )}
          {tracksError && <div className="recommendations-error">{tracksError}</div>}
          {!tracksLoading && !tracksError && popularTracks.length === 0 && (
            <div className="recommendations-empty">
              <span className="recommendations-empty-icon">🛤️</span>
              <p>{t('bookClub.recommendations.noPopularTracks')}</p>
            </div>
          )}
          {!tracksLoading && !tracksError && popularTracks.length > 0 && (
            <div className="recommendations-tracks-grid">
              {popularTracks.map((track) => (
                <div key={track.trackId} className="recommendations-track-card">
                  <div className="recommendations-track-header">
                    <h4 className="recommendations-track-title">{track.title}</h4>
                    <span className={`recommendations-track-badge recommendations-track-badge-${DIFFICULTY_COLORS[track.difficulty] || 'blue'}`}>
                      {track.difficulty}
                    </span>
                  </div>
                  <span className="recommendations-track-topic">{track.topic}</span>
                  <div className="recommendations-track-meta">
                    <span>👥 {track.enrollmentCount} {t('bookClub.recommendations.enrolled')}</span>
                    <span>📚 {track.bookCount} {t('bookClub.common.books')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Recommendations;
